import { useMemo, useState, type ReactNode, type MouseEvent } from 'react';
import { Col, Modal, Radio, Row, Space, Typography, message } from 'antd';
import { useAreasData } from '../../hooks/data/useAreasData';
import { useCustomAreas } from '../../hooks/SimViewer/useCustomAreas';
import { useAggregationConfig } from '../../hooks/SimViewer/useAggregationConfig';
import { useFeatureOptionsData } from '../../hooks/data/useFeatureOptionsData';
import type { Area, CustomArea } from '../../types/domain';
import { useTranslation } from 'react-i18next';
import { InfoPopoverButton } from '../InfoPopoverButton';
import { useDataFilter } from '../../hooks/SimViewer/useDataFilter';

type Props = {
  areaKeyCodes: string[];
  customAreaKeys: string[];
  isOpen: boolean;
  onClose: () => void;
};

type TripFilter = 'start' | 'end' | 'excursion' | 'pass' | null;

type AgentFilter = 'home' | 'work' | 'visit' | null;

type AreaFilters = Record<
  'start' | 'end' | 'pass' | 'home' | 'work' | 'visit',
  boolean
>;

const toTripFilter = (filter: AreaFilters): TripFilter => {
  if (filter.start && filter.end) {
    return 'excursion';
  }
  if (filter.start) {
    return 'start';
  }
  if (filter.end) {
    return 'end';
  }
  if (filter.pass) {
    return 'pass';
  }
  return null;
};

const toAgentFilter = (filter: AreaFilters): AgentFilter => {
  if (filter.home) {
    return 'home';
  }
  if (filter.work) {
    return 'work';
  }
  if (filter.visit) {
    return 'visit';
  }
  return null;
};

const AreaFilterModal = ({
  areaKeyCodes,
  customAreaKeys,
  isOpen,
  onClose,
}: Props): ReactNode => {
  const { dataFilter, updateAreaFilter, updateCustomAreaFilter } =
    useDataFilter();
  const { t } = useTranslation();
  const { featureOptions } = useFeatureOptionsData();
  const { findArea } = useAreasData();
  const { getCustomAreaByKey } = useCustomAreas();
  const { startAggregation, aggregationConfig } = useAggregationConfig();

  const selectedAreas = useMemo((): Area[] => {
    return areaKeyCodes
      .map((keyCode: string): Area | null => {
        return findArea(keyCode);
      })
      .filter((area: Area | null): area is Area => {
        return !!area;
      });
  }, [areaKeyCodes, findArea]);

  const selectedCustomAreas = useMemo((): CustomArea[] => {
    return customAreaKeys
      .map((key): CustomArea | undefined => {
        return getCustomAreaByKey(key);
      })
      .filter(
        (customArea: CustomArea | undefined): customArea is CustomArea => {
          return !!customArea;
        }
      );
  }, [customAreaKeys, getCustomAreaByKey]);

  const [defaultTripFilter, defaultAgentFilter] = useMemo((): [
    TripFilter,
    AgentFilter,
  ] => {
    const appliedFilter = [
      ...selectedAreas.map((area: Area): AreaFilters => {
        return {
          start: dataFilter.start.areaKeyCodes.includes(area.key_code),
          end: dataFilter.end.areaKeyCodes.includes(area.key_code),
          pass: dataFilter.pass.areaKeyCodes.includes(area.key_code),
          home: dataFilter.home.areaKeyCodes.includes(area.key_code),
          work: dataFilter.work.areaKeyCodes.includes(area.key_code),
          visit: dataFilter.visit.areaKeyCodes.includes(area.key_code),
        };
      }),
      ...selectedCustomAreas.map((customArea: CustomArea): AreaFilters => {
        return {
          start: dataFilter.start.customAreaKeys.includes(customArea.key),
          end: dataFilter.end.customAreaKeys.includes(customArea.key),
          pass: dataFilter.pass.customAreaKeys.includes(customArea.key),
          home: dataFilter.home.customAreaKeys.includes(customArea.key),
          work: dataFilter.work.customAreaKeys.includes(customArea.key),
          visit: dataFilter.visit.customAreaKeys.includes(customArea.key),
        };
      }),
    ].reduce((result: AreaFilters | null, filter: AreaFilters): AreaFilters => {
      if (!result) {
        return filter;
      }
      // 共通のフィルター条件を持つものだけを残す
      return {
        start: result.start && filter.start,
        end: result.end && filter.end,
        pass: result.pass && filter.pass,
        home: result.home && filter.home,
        work: result.work && filter.work,
        visit: result.visit && filter.visit,
      };
    }, null);

    return appliedFilter
      ? [toTripFilter(appliedFilter), toAgentFilter(appliedFilter)]
      : [null, null];
  }, [
    dataFilter.start,
    dataFilter.end,
    dataFilter.pass,
    dataFilter.home,
    dataFilter.work,
    dataFilter.visit,
    selectedAreas,
    selectedCustomAreas,
  ]);

  const [tripFilter, setTripFilter] = useState<TripFilter>(defaultTripFilter);
  const [agentFilter, setAgentFilter] =
    useState<AgentFilter>(defaultAgentFilter);

  const areaName = useMemo((): string => {
    const selectedNames = [
      ...selectedAreas.map((area) => area.s_name || area.key_code),
      ...selectedCustomAreas.map(
        (customArea) => customArea.label || customArea.key
      ),
    ];

    if (selectedNames.length <= 5) {
      return selectedNames.join(t('listDelimiter'));
    }
    return `${selectedNames.slice(0, 5).join(t('listDelimiter'))} ... ${t(
      'listOthers',
      {
        count: selectedNames.length - 5,
      }
    )}`;
  }, [selectedAreas, selectedCustomAreas, t]);

  const handleClickOk = (): void => {
    // フィルター条件を更新する
    const condition = {
      start: tripFilter === 'start' || tripFilter === 'excursion',
      end: tripFilter === 'end' || tripFilter === 'excursion',
      pass: tripFilter === 'pass',
      home: agentFilter === 'home',
      work: agentFilter === 'work',
      visit: agentFilter === 'visit',
    };
    if (selectedAreas.length > 0) {
      updateAreaFilter(
        selectedAreas.map((area) => area.key_code),
        condition
      );
    }
    if (selectedCustomAreas.length > 0) {
      updateCustomAreaFilter(
        selectedCustomAreas.map((customArea) => customArea.key),
        condition
      );
    }

    // 地図上の可視化が設定されていない場合は、条件に応じて可視化を設定する
    if (!aggregationConfig) {
      if (tripFilter === 'end') {
        startAggregation('start');
      } else if (tripFilter === 'start') {
        startAggregation('end');
      } else if (tripFilter === 'excursion') {
        startAggregation('trip_path');
      }
    }

    message.success(t('message.analysisConditionApplied', { name: areaName }));
    onClose();
  };

  const isSelectingMultipleAreas =
    selectedAreas.length + selectedCustomAreas.length > 1;
  const isFilterUnchanged =
    tripFilter === defaultTripFilter && agentFilter === defaultAgentFilter;

  return (
    <Modal
      title={areaName}
      centered
      okText={t('applyCondition')}
      cancelButtonProps={{ style: { display: 'none' } }}
      okButtonProps={{ size: 'small', disabled: isFilterUnchanged }}
      mask={false}
      keyboard={false}
      width={600}
      open={isOpen}
      onCancel={onClose}
      onOk={handleClickOk}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Typography.Text style={{ display: 'block' }}>
          {t('message.selectAnalysisCondition')}
        </Typography.Text>
      </div>
      <Row style={{ marginTop: 16 }}>
        <Col span={14}>
          <Typography.Text strong style={{ display: 'block' }}>
            {t('filterBy.move')}
          </Typography.Text>
          <Space
            direction="vertical"
            style={{ width: '100%', marginTop: 8, marginLeft: 8 }}
          >
            <Radio
              checked={tripFilter === 'end'}
              onClick={(event: MouseEvent): void => {
                event.preventDefault();
                setTripFilter((currentFilter: TripFilter): TripFilter => {
                  return currentFilter === 'end' ? null : 'end';
                });
              }}
            >
              {t('arrived', {
                place: isSelectingMultipleAreas ? t('theseAreas') : areaName,
              })}
              <InfoPopoverButton content={t('additionalInfos.arrival')} />
            </Radio>
            <Radio
              checked={tripFilter === 'start'}
              onClick={(event: MouseEvent): void => {
                event.preventDefault();
                setTripFilter((currentFilter: TripFilter): TripFilter => {
                  return currentFilter === 'start' ? null : 'start';
                });
              }}
            >
              {t('departed', {
                place: isSelectingMultipleAreas ? t('theseAreas') : areaName,
              })}
              <InfoPopoverButton content={t('additionalInfos.departure')} />
            </Radio>
            <Radio
              checked={tripFilter === 'excursion'}
              onClick={(event: MouseEvent): void => {
                event.preventDefault();
                setTripFilter((currentFilter: TripFilter): TripFilter => {
                  return currentFilter === 'excursion' ? null : 'excursion';
                });
              }}
            >
              {t('excursioned', {
                place: isSelectingMultipleAreas ? t('theseAreas') : areaName,
              })}
              <InfoPopoverButton content={t('additionalInfos.excursion')} />
            </Radio>
            {featureOptions?.use_passed_routes_view && (
              <Radio
                checked={tripFilter === 'pass'}
                onClick={(event: MouseEvent): void => {
                  event.preventDefault();
                  setTripFilter((currentFilter: TripFilter): TripFilter => {
                    return currentFilter === 'pass' ? null : 'pass';
                  });
                }}
              >
                {t('passed', {
                  place: isSelectingMultipleAreas ? t('theseAreas') : areaName,
                })}
              </Radio>
            )}
          </Space>
        </Col>

        {(featureOptions?.use_home_agents ||
          featureOptions?.use_work_agents) && (
          <Col span={10}>
            <Typography.Text strong style={{ display: 'block' }}>
              {t('filterBy.features')}
            </Typography.Text>
            <Space
              direction="vertical"
              style={{ width: '100%', marginTop: 8, marginLeft: 8 }}
            >
              {featureOptions?.use_home_agents && (
                <Radio
                  checked={agentFilter === 'home'}
                  onClick={(event: MouseEvent): void => {
                    event.preventDefault();
                    setAgentFilter(
                      (currentFilter: AgentFilter): AgentFilter => {
                        return currentFilter === 'home' ? null : 'home';
                      }
                    );
                  }}
                >
                  {t('features.resident')}
                </Radio>
              )}
              {featureOptions?.use_work_agents && (
                <Radio
                  checked={agentFilter === 'work'}
                  onClick={(event: MouseEvent): void => {
                    event.preventDefault();
                    setAgentFilter(
                      (currentFilter: AgentFilter): AgentFilter => {
                        return currentFilter === 'work' ? null : 'work';
                      }
                    );
                  }}
                >
                  {t('features.worker')}
                </Radio>
              )}
              <Radio
                checked={agentFilter === 'visit'}
                onClick={(event: MouseEvent): void => {
                  event.preventDefault();
                  setAgentFilter((currentFilter: AgentFilter): AgentFilter => {
                    return currentFilter === 'visit' ? null : 'visit';
                  });
                }}
              >
                {t('features.visitor')}
                <InfoPopoverButton content={t('additionalInfos.visitor')} />
              </Radio>
            </Space>
          </Col>
        )}
      </Row>
    </Modal>
  );
};

export { AreaFilterModal };
